import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UserAvatar } from "@/components/UserAvatar";
import { AlertTriangle, ArrowLeft, CheckCircle2, Clock, Copy, Pencil, Phone, Plus, ShieldCheck, User } from "lucide-react";
import { fetchRun, useAsync } from "@/lib/queries";
import { ENGINEER_JOB_TYPES, formatDate, formatTime, progressOf } from "@/lib/constants";
import { StatusBadge, ProgressBar } from "@/components/StatusBadge";
import { ErrorState, LoadingRows, usePageTitle } from "@/components/States";
import { AddressCard } from "@/components/AddressCard";
import { Attachments } from "@/components/Attachments";
import { AddAddressDialog } from "@/components/AddAddressDialog";
import { Button } from "@/components/ui/button";
import { ExportRunMenu } from "@/components/ExportRunMenu";
import { ChangeLog } from "@/components/ChangeLog";
import { OfficerSignature } from "@/components/OfficerSignature";
import { RunMiniMap } from "@/components/map/RunMiniMap";
import { MapsUpgradeCard } from "@/components/map/MapsUpgradeCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Info = ({ icon: Icon, label, value, sub, testId, avatar }) => (
  <div className="flex items-start gap-3">
    {avatar || <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Icon className="h-4 w-4" /></div>}
    <div className="min-w-0">
      <p className="font-data text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="truncate font-semibold text-slate-900" data-testid={testId}>{value}</p>
      {sub}
    </div>
  </div>
);

export default function RunDetail({ backTo, isManager = false }) {
  const { profile } = useAuth();
  const { id } = useParams();
  usePageTitle("Warrant Run");
  const { data: run, loading, error, reload, setData } = useAsync(() => fetchRun(id), [id]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const detailBackTo = backTo || (profile?.role === "manager" ? "/manager/runs" : "/jobs");

  if (loading && !run) return <LoadingRows rows={5} testId="run-detail-loading" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const p = progressOf(run.run_addresses);
  const warrantTypes = Array.isArray(run.warrant_types) ? run.warrant_types : ["locksmith"];
  const engineerJobTypes = profile?.company?.engineer_job_types ?? ENGINEER_JOB_TYPES;
  const showLocksmith = warrantTypes.includes("locksmith");
  const profileTypes = profile?.user_types?.length ? profile.user_types : [profile?.role];
  const assignedAgent = profileTypes.includes("agent") && profile.id === run.officer_id && warrantTypes.includes("agent");
  const hasRunFiles = (run.run_files || []).length > 0;
  const selectedAgent = run.officer?.role === "agent" && warrantTypes.includes("agent");
  const showRunFiles = assignedAgent || (isManager && selectedAgent) || hasRunFiles;
  const replaceAddress = (a) => {
    setData({ ...run, run_addresses: run.run_addresses.map((x) => (x.id === a.id ? a : x)) });
    fetchRun(id).then((fresh) => setData({ ...fresh })).catch(() => {});
  };

  return (
    <div className="space-y-4" data-testid="run-detail-page">
      <div className="flex items-center justify-between gap-2">
        <Link to={detailBackTo} className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-blue-700 hover:underline" data-testid="run-detail-back-link"><ArrowLeft className="h-4 w-4" /> Back</Link>
        {isManager && <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              await navigator.clipboard.writeText(`${window.location.origin}/runs/${run.id}`);
              toast.success("Run link copied");
            } catch {
              toast.error("Couldn't copy the run link");
            }
          }} data-testid="run-detail-share-button"><Copy className="mr-1 h-4 w-4" /> Copy link</Button>
          <ExportRunMenu runId={run.id} /><Button asChild variant="outline" size="sm" data-testid="run-detail-edit-button"><Link to={`/manager/runs/${run.id}/edit`}><Pencil className="mr-1 h-4 w-4" /> Edit run</Link></Button>
        </div>}
      </div>
      {run.stab_vest_required && (
        <div role="alert" className="flex items-center gap-3 rounded-xl border-2 border-red-300 bg-red-50 p-4 text-red-900" data-testid="run-stab-vest-warning">
          <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
          <div>
            <p className="font-display text-base font-extrabold uppercase tracking-wide">Stab vest required</p>
            <p className="text-sm">You must wear a stab vest on this run.</p>
          </div>
        </div>
      )}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-data text-xs uppercase tracking-[0.16em] text-blue-700">Warrant run</p>
            <h1 className="font-display mt-1 text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="run-detail-date">{formatDate(run.run_date)}</h1>
          </div>
          <StatusBadge status={run.status} testId="run-detail-status" />
        </div>
        <div className={`mt-5 grid gap-4 ${showLocksmith ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          <Info icon={Clock} label="Start time" value={formatTime(run.start_time)} testId="run-detail-start-time" />
          <Info icon={ShieldCheck} label="Officer" value={run.officer?.full_name || "—"} testId="run-detail-officer"
            sub={run.officer?.phone && <a href={`tel:${run.officer.phone}`} className="flex items-center gap-1 text-sm text-blue-700"><Phone className="h-3 w-3" />{run.officer.phone}</a>} />
          {showLocksmith && <Info icon={User} label="Locksmith" value={run.locksmith?.full_name || run.locksmith?.email || "Open job, waiting for a locksmith to accept"} testId="run-detail-locksmith"
            avatar={run.locksmith_id && run.locksmith?.avatar_updated_at && <UserAvatar userId={run.locksmith_id} stamp={run.locksmith.avatar_updated_at} name={run.locksmith.full_name} className="h-9 w-9" testId="run-detail-locksmith-avatar" />} />
          }
        </div>
        {isManager && run.accepted_at && run.locksmith && (
          <p className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="run-detail-taken-by">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>Taken by <strong>{run.locksmith.full_name || run.locksmith.email}</strong> on {new Date(run.accepted_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}. This was an open job.</span>
          </p>
        )}
        {run.notes && <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{run.notes}</p>}
        <OfficerSignature run={run} onSigned={reload} />
        {profile?.company?.maps_enabled !== false && <div className="mt-4"><RunMiniMap runId={run.id} refreshKey={run} /></div>}
        {profile?.company?.maps_enabled === false && isManager && <MapsUpgradeCard className="mt-4 min-h-[220px]" testId="run-maps-upgrade" />}
      </div>
      {showRunFiles && <Attachments runId={run.id} runFiles={run.run_files} testId="run-detail" onChanged={reload}
        canUpload={assignedAgent || (isManager && selectedAgent)} title="Warrant files" />}
      <div className="sticky top-14 z-20 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-sm backdrop-blur md:top-2">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-semibold text-slate-800">Progress</span>
          <span className="font-data text-slate-600" data-testid="run-detail-progress-pct">{p.pct}%</span>
        </div>
        <ProgressBar done={p.done} total={p.total} testId="run-detail-progress" />
      </div>
      <div className="space-y-3">
        {run.run_addresses.map((a, i) => <AddressCard key={a.id} address={a} index={i} onSaved={replaceAddress} onFilesChanged={reload} onEdit={setEditing} isManager={isManager} runDate={run.run_date} warrantTypes={warrantTypes} />)}
      </div>
      <Button onClick={() => setAdding(true)} variant="outline" className="h-12 w-full border-dashed text-base" data-testid="run-detail-add-address-button">
        <Plus className="mr-2 h-5 w-5" /> Add address to this run
      </Button>
      {isManager && <ChangeLog runId={run.id} refreshKey={run} />}
      <AddAddressDialog open={adding} onOpenChange={setAdding} runId={run.id} onAdded={reload} warrantTypes={warrantTypes} runEngineerId={run.engineer_id} jobTypes={engineerJobTypes} />
      <AddAddressDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} runId={run.id} address={editing} onAdded={replaceAddress} warrantTypes={warrantTypes} runEngineerId={run.engineer_id} jobTypes={engineerJobTypes} />
    </div>
  );
}
